import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Shield,
  Pencil,
  Trash2,
  X,
  LayoutDashboard,
  Wallet,
  HandCoins,
  Users,
  Briefcase,
  Receipt,
  BarChart3,
  UserCog,
  Settings as SettingsIcon,
  Check,
  Eye,
  PencilLine,
  type LucideIcon,
} from 'lucide-react';
import { loadDevJson, saveDevJson } from '../../lib/devStorage';
import { PageSearchBar } from '../common/PageSearchBar';
import { workspaceAdminsApi, hasAuthToken, ApiError, type WorkspaceAdminRecord } from '../../api';

const KEY = 'workspace_admins_v1';

export type AccessLevel = 'none' | 'read' | 'write';
export type AdminRole = 'super_admin' | 'admin' | 'ops' | 'auditor';
export type Permissions = Record<string, AccessLevel>;

export type WorkspaceAdmin = {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  phone: string;
  permissions: Permissions;
  isOrgOwner?: boolean;
};

interface ModuleDef {
  id: string;
  label: string;
  icon: LucideIcon;
}

const MODULES: ModuleDef[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'finance', label: 'Finance', icon: Wallet },
  { id: 'loans', label: 'Hand loans', icon: HandCoins },
  { id: 'customers', label: 'Customers', icon: Users },
  { id: 'consultancy', label: 'Consultancy', icon: Briefcase },
  { id: 'employees', label: 'Employees & payroll', icon: UserCog },
  { id: 'expenses', label: 'Expenses', icon: Receipt },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
  { id: 'audit', label: 'Audit logs', icon: Shield },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

const ROLE_LABEL: Record<AdminRole, string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
  ops: 'Operations',
  auditor: 'Auditor',
};

/** Sensible baseline permissions applied when a role is chosen. */
function defaultsForRole(role: AdminRole): Permissions {
  const all = (lvl: AccessLevel): Permissions =>
    Object.fromEntries(MODULES.map((m) => [m.id, lvl]));
  if (role === 'super_admin') return all('write');
  if (role === 'ops') {
    const p = all('read');
    p.employees = 'none';
    p.expenses = 'none';
    p.settings = 'none';
    return p;
  }
  if (role === 'auditor') {
    const p = all('read');
    p.settings = 'none';
    p.audit = 'read';
    return p;
  }
  // admin: write everywhere except settings (read)
  const p = all('write');
  p.settings = 'read';
  return p;
}

function normalize(a: Partial<WorkspaceAdmin>): WorkspaceAdmin {
  const role = (a.role as AdminRole) || 'admin';
  const perms: Permissions = {};
  for (const m of MODULES) perms[m.id] = (a.permissions && a.permissions[m.id]) || 'none';
  return {
    id: a.id || crypto.randomUUID(),
    name: a.name || '',
    email: a.email || '',
    role,
    phone: a.phone || '',
    permissions: perms,
  };
}

function readAdmins(): WorkspaceAdmin[] {
  return loadDevJson<WorkspaceAdmin[]>(KEY, []).map(normalize);
}

function writeAdmins(list: WorkspaceAdmin[]) {
  saveDevJson(KEY, list);
}

function fromApi(a: WorkspaceAdminRecord): WorkspaceAdmin {
  return normalize({
    id: a.id,
    name: a.name,
    email: a.email,
    phone: a.phone,
    role: a.deskRole,
    permissions: a.permissions,
    isOrgOwner: a.isOrgOwner,
  });
}

const emptyForm = (): Omit<WorkspaceAdmin, 'id'> & { password: string; confirmPassword: string } => ({
  name: '',
  email: '',
  role: 'admin',
  phone: '',
  permissions: defaultsForRole('admin'),
  password: '',
  confirmPassword: '',
});

/* ----------------------------- small controls ----------------------------- */

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        on ? 'bg-emerald-600' : 'bg-zinc-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          on ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function LevelToggle({ level, onChange }: { level: AccessLevel; onChange: (l: AccessLevel) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-zinc-200 bg-zinc-50 p-0.5">
      <button
        type="button"
        onClick={() => onChange('read')}
        className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition ${
          level === 'read' ? 'bg-white text-zinc-900 shadow-sm ring-1 ring-zinc-200' : 'text-zinc-500 hover:text-zinc-700'
        }`}
      >
        <Eye className="h-3.5 w-3.5" />
        Read
      </button>
      <button
        type="button"
        onClick={() => onChange('write')}
        className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition ${
          level === 'write' ? 'bg-emerald-600 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
        }`}
      >
        <PencilLine className="h-3.5 w-3.5" />
        Write
      </button>
    </div>
  );
}

/* -------------------------------- component -------------------------------- */

const WorkspaceAdminsSettings: React.FC = () => {
  const [admins, setAdmins] = useState<WorkspaceAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [synced, setSynced] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setApiError(null);
      try {
        if (hasAuthToken()) {
          const res = await workspaceAdminsApi.list();
          if (active) {
            setAdmins(res.admins.map(fromApi));
            setSynced(true);
          }
        } else {
          if (active) {
            setAdmins(readAdmins());
            setSynced(false);
          }
        }
      } catch (e) {
        if (active) {
          setAdmins(readAdmins());
          setSynced(false);
          setApiError(e instanceof ApiError ? e.message : 'Could not load admins from the server.');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return admins;
    return admins.filter((a) => [a.name, a.email, a.phone, a.role].join(' ').toLowerCase().includes(q));
  }, [admins, search]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (a: WorkspaceAdmin) => {
    setEditingId(a.id);
    setForm({ name: a.name, email: a.email, role: a.role, phone: a.phone, permissions: { ...a.permissions }, password: '', confirmPassword: '' });
    setModalOpen(true);
  };

  const persistLocal = (next: WorkspaceAdmin[]) => {
    setAdmins(next);
    if (!synced) writeAdmins(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;

    // Password is required on create and optional on edit. Whatever they type is accepted.
    const wantsPassword = !editingId || !!form.password || !!form.confirmPassword;
    if (wantsPassword) {
      if (!editingId && !form.password.trim()) {
        setApiError('Set a password for the new admin.');
        return;
      }
      if (form.password !== form.confirmPassword) {
        setApiError('Password and confirm password do not match.');
        return;
      }
    }

    setSaving(true);
    setApiError(null);
    setCreatedPassword(null);
    try {
      if (synced && hasAuthToken()) {
        if (editingId) {
          const res = await workspaceAdminsApi.update(editingId, {
            name: form.name,
            phone: form.phone,
            deskRole: form.role,
            permissions: form.permissions,
            ...(form.password ? { password: form.password } : {}),
          });
          setAdmins((list) => list.map((a) => (a.id === editingId ? fromApi(res.admin) : a)));
        } else {
          const res = await workspaceAdminsApi.create({
            name: form.name,
            email: form.email,
            phone: form.phone,
            deskRole: form.role,
            permissions: form.permissions,
            password: form.password || undefined,
          });
          setAdmins((list) => [fromApi(res.admin), ...list]);
          if (res.temporaryPassword) setCreatedPassword(res.temporaryPassword);
        }
      } else if (editingId) {
        persistLocal(admins.map((a) => (a.id === editingId ? normalize({ ...a, ...form }) : a)));
      } else {
        persistLocal([normalize({ id: crypto.randomUUID(), ...form }), ...admins]);
      }
      setModalOpen(false);
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Could not save admin.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    const target = admins.find((a) => a.id === id);
    if (target?.isOrgOwner) return;
    if (!window.confirm('Remove this workspace admin?')) return;
    setApiError(null);
    try {
      if (synced && hasAuthToken()) {
        await workspaceAdminsApi.remove(id);
        setAdmins((list) => list.filter((a) => a.id !== id));
      } else {
        persistLocal(admins.filter((a) => a.id !== id));
      }
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Could not remove admin.');
    }
  };

  const setLevel = (moduleId: string, level: AccessLevel) =>
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [moduleId]: level } }));

  const setAll = (level: AccessLevel) =>
    setForm((f) => ({ ...f, permissions: Object.fromEntries(MODULES.map((m) => [m.id, level])) }));

  const grantedCount = (a: WorkspaceAdmin) => MODULES.filter((m) => a.permissions[m.id] !== 'none').length;
  const writeCount = (a: WorkspaceAdmin) => MODULES.filter((m) => a.permissions[m.id] === 'write').length;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2 text-zinc-900">
          <Shield className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" aria-hidden />
          <div>
            <p className="text-sm font-medium">Workspace administrators</p>
            <p className="text-xs text-zinc-500">
              {synced
                ? 'Synced to your workspace — permissions are enforced on the server for every API call.'
                : 'Offline mode — stored on this device until you sign in to the workspace API.'}
            </p>
          </div>
        </div>
        <button type="button" onClick={openCreate} className="app-btn-primary inline-flex shrink-0 items-center gap-2">
          <Plus className="h-4 w-4" />
          Add admin
        </button>
      </div>

      {apiError && <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">{apiError}</p>}

      {createdPassword && (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
          Admin created. Temporary password: <strong className="font-mono">{createdPassword}</strong> — share securely with the user.
        </p>
      )}

      {loading ? (
        <p className="py-8 text-center text-sm text-zinc-500">Loading workspace admins…</p>
      ) : (
        <>
          <PageSearchBar value={search} onChange={setSearch} placeholder="Search name, email, role…" className="max-w-full" />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {filtered.map((a) => (
          <div
            key={a.id}
            className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm transition-shadow hover:border-zinc-300"
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-sm font-bold text-emerald-800">
                {a.name.slice(0, 1).toUpperCase() || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-sm font-semibold text-zinc-900">{a.name}</h3>
                <p className="truncate text-xs text-zinc-500">{a.email}</p>
                <p className="mt-0.5 truncate text-xs text-zinc-600">{a.phone || '—'}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <button type="button" onClick={() => openEdit(a)} className="rounded-md p-2 text-zinc-600 hover:bg-zinc-100" aria-label={`Edit ${a.name}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => remove(a.id)} disabled={a.isOrgOwner} className="rounded-md p-2 text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40" aria-label={`Remove ${a.name}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-700">
                <Shield className="h-3 w-3" />
                {a.isOrgOwner ? 'Owner' : ROLE_LABEL[a.role]}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                {grantedCount(a)} module{grantedCount(a) === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
                {writeCount(a)} write
              </span>
            </div>

            {grantedCount(a) > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {MODULES.filter((m) => a.permissions[m.id] !== 'none').map((m) => {
                  const Icon = m.icon;
                  const write = a.permissions[m.id] === 'write';
                  return (
                    <span
                      key={m.id}
                      className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${
                        write ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-zinc-200 bg-zinc-50 text-zinc-600'
                      }`}
                      title={`${m.label}: ${write ? 'Read & write' : 'Read only'}`}
                    >
                      <Icon className="h-3 w-3" />
                      {m.label}
                      {write ? <PencilLine className="h-2.5 w-2.5" /> : <Eye className="h-2.5 w-2.5" />}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {admins.length === 0 && (
        <p className="rounded-md border border-dashed border-zinc-200 py-8 text-center text-sm text-zinc-500">
          No admins yet. Add people and choose exactly which modules they can view or edit.
        </p>
      )}

          {admins.length > 0 && filtered.length === 0 && (
            <p className="text-center text-sm text-zinc-500">No matches for your search.</p>
          )}
        </>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-zinc-950/40 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="app-card flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-b-none shadow-lg sm:max-h-[92vh] sm:rounded-2xl">
            <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-4 py-3 sm:px-5">
              <h3 className="text-sm font-semibold text-zinc-900">{editingId ? 'Edit admin & permissions' : 'Add admin & permissions'}</h3>
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4 sm:px-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="app-label">Name</label>
                    <input className="app-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div>
                    <label className="app-label">Email</label>
                    <input
                      type="email"
                      className="app-field"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      disabled={!!editingId}
                    />
                  </div>
                  <div>
                    <label className="app-label">Phone</label>
                    <input className="app-field" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <div>
                    <label className="app-label">{editingId ? 'New password' : 'Password'}</label>
                    <input
                      type="password"
                      className="app-field"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder={editingId ? 'Leave blank to keep current' : 'Any password you choose'}
                      autoComplete="new-password"
                      required={!editingId}
                    />
                  </div>
                  <div>
                    <label className="app-label">Confirm password</label>
                    <input
                      type="password"
                      className="app-field"
                      value={form.confirmPassword}
                      onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                      placeholder="Re-enter password"
                      autoComplete="new-password"
                      required={!editingId}
                    />
                    {form.confirmPassword && form.password !== form.confirmPassword && (
                      <p className="mt-1 text-[11px] font-medium text-rose-600">Passwords do not match.</p>
                    )}
                  </div>
                  <div>
                    <label className="app-label">Role</label>
                    <select
                      className="app-field"
                      value={form.role}
                      onChange={(e) => {
                        const role = e.target.value as AdminRole;
                        setForm((f) => ({ ...f, role, permissions: defaultsForRole(role) }));
                      }}
                    >
                      <option value="super_admin">Super admin</option>
                      <option value="admin">Admin</option>
                      <option value="ops">Operations</option>
                      <option value="auditor">Auditor (read-only)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">Module permissions</p>
                      <p className="text-xs text-zinc-500">Toggle access per area, then choose Read or Write.</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <button type="button" onClick={() => setAll('write')} className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-50">
                        Grant all
                      </button>
                      <button type="button" onClick={() => setAll('read')} className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-50">
                        Read only
                      </button>
                      <button type="button" onClick={() => setAll('none')} className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-50">
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200">
                    {MODULES.map((m) => {
                      const Icon = m.icon;
                      const level = form.permissions[m.id] ?? 'none';
                      const on = level !== 'none';
                      return (
                        <div key={m.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                          <div className="flex min-w-0 items-center gap-2.5">
                            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${on ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-400'}`}>
                              <Icon className="h-[18px] w-[18px]" />
                            </span>
                            <span className={`truncate text-sm font-medium ${on ? 'text-zinc-900' : 'text-zinc-500'}`}>{m.label}</span>
                          </div>
                          <div className="flex shrink-0 items-center gap-2.5">
                            {on && <LevelToggle level={level} onChange={(l) => setLevel(m.id, l)} />}
                            <Toggle on={on} onChange={(v) => setLevel(m.id, v ? 'read' : 'none')} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 justify-end gap-2 border-t border-zinc-200 bg-white px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-5 sm:pb-3">
                <button type="button" onClick={() => setModalOpen(false)} className="app-btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="app-btn-primary inline-flex items-center gap-1.5 disabled:opacity-60">
                  <Check className="h-4 w-4" />
                  {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceAdminsSettings;
